import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { API_URL, clearStorageKeepEod } from '../utils/utils.js'

const handleUnauthorizedRedirection = (route = "") => {
  if (route.includes("users/signin") || route.includes("auth/login")) {
    return;
  }
  clearStorageKeepEod();
  const publicPaths = ['/login', '/signup', '/reset-password', '/forgot-password', '/privacy-policy', '/terms-and-conditions', '/'];
  const isPublic = publicPaths.some(p => p === '/' ? window.location.pathname === '/' : window.location.pathname.startsWith(p));
  if (!isPublic) {
    toast.error("Session timed out. Please login to continue.");
    window.location.href = '/login';
  }
};

const handleFetchError = (e, responseStatus = null) => {
  throw e;
};

const handleUploadError = (e) => {
  return {
    success: false,
    message: e?.message || 'Something went wrong. Please try again.'
  };
};

const getData = async (route, params = {}, explicitToken = null) => {
  try {
    let actualParams = params;
    let token = explicitToken;

    if (typeof params === "string" && !explicitToken) {
      token = params;
      actualParams = {};
    }

    const URL_ROUTE = `${API_URL}${route}`;

    let queryParams = "";
    if (actualParams && typeof actualParams === "object" && Object.keys(actualParams).length > 0) {
      queryParams = new URLSearchParams(actualParams).toString();
    }

    let finalUrl = URL_ROUTE;
    if (queryParams) {
      finalUrl += finalUrl.includes("?") ? `&${queryParams}` : `?${queryParams}`;
    }

    const sessionToken = token || sessionStorage.getItem("Token") || localStorage.getItem("Token");

    const response = await fetch(finalUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`
      }
    });

    if (response.status === 401) {
      handleUnauthorizedRedirection(route);
      return;
    }

    const responseData = await response.json();
    if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
      window.location.href = '/subscription-expired';
      return responseData;
    }
    return responseData;
  } catch (e) {
    console.error("API Error:", e);
    handleFetchError(e);
  }
};


const postData = async (route, data, token = null, isFormData = false) => {
  try {
    if (!API_URL) {
      throw new Error("❌ API_URL is not defined. Please check your environment config.");
    }
    if (!route) {
      throw new Error("❌ Route is required for postData.");
    }

    const URL_ROUTE = `${API_URL.replace(/\/+$/, "")}/${route.replace(/^\/+/, "")}`;

    const headers = {};
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(URL_ROUTE, {
      method: "POST",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });

    console.log("Response URL:", URL_ROUTE);
    console.log("Response Status:", response.status);

    if (response.status === 401 && !route.includes("users/signin")) {
      handleUnauthorizedRedirection(route);
      return {
        success: false,
        message: "Session timed out. Please login to continue."
      };
    }

    const contentType = response.headers.get("content-type");
    let responseData;

    if (contentType && contentType.includes("application/json")) {
      responseData = await response.json();
      if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
        window.location.href = '/subscription-expired';
        return responseData;
      }
    } else {
      const text = await response.text();
      console.error("Non-JSON response:", text);
      return {
        success: false,
        message: `Server returned non-JSON response (${response.status})`
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: responseData?.message || "Request failed"
      };
    }

    return responseData;
  } catch (e) {
    console.error("Unexpected Error:", e.message);
    handleFetchError(e);
  }
};

const postDataBulkUpload = async (route, data, token) => {
  try {
    const URL_ROUTE = `${API_URL}${route}`.replace(/\/+$/, "");

    console.log("Sending Request to:", URL_ROUTE);
    console.log("Request Body:", data);

    const response = await fetch(URL_ROUTE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: data,
    });

    if (response.status === 401) {
      handleUnauthorizedRedirection(route);
      return;
    }

    console.log("Response Status:", response.status);

    const responseData = await response.json();
    if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
      window.location.href = '/subscription-expired';
      return responseData;
    }

    if (!response.ok) {
      console.error("Response Error:", responseData);
      return responseData;
    }

    console.log("Response Data:", responseData);
    return responseData;
  } catch (e) {
    console.error("Unexpected Error:", e.message);
    handleFetchError(e);
  }
};

const patchData = async (route, body = {}) => {
  try {
    const URL_ROUTE = `${API_URL}${route}`;
    const sessionToken = sessionStorage.getItem("Token") || localStorage.getItem("Token");

    const response = await fetch(URL_ROUTE, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${sessionToken}`
      },
      body: JSON.stringify(body)
    });

    if (response.status === 401) {
      handleUnauthorizedRedirection(route);
      return;
    }

    if (!response.ok) {
      const responseData = await response.json().catch(() => ({}));
      if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
        window.location.href = '/subscription-expired';
        return responseData;
      }
      return {
        success: false,
        message: responseData?.message || `Error: ${response.status} - ${response.statusText}`
      };
    }

    return await response.json();
  } catch (e) {
    console.error("API Error:", e);
    handleFetchError(e);
  }
};


const putData = async (route, data, token = null) => {
  try {
    const URL_ROUTE = `${API_URL}${route}`.replace(/\/+$/, "");

    console.log("Sending PUT Request to:", URL_ROUTE);
    console.log("Request Body:", JSON.stringify(data));
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(URL_ROUTE, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      handleUnauthorizedRedirection(route);
      return;
    }

    console.log("Response Status:", response.status);

    const responseData = await response.json();
    if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
      window.location.href = '/subscription-expired';
      return responseData;
    }

    if (!response.ok) {
      console.error("Response Error:", responseData);
      return {
        success: false,
        message: responseData?.message || `Error: ${response.status}`
      };
    }

    console.log("Response Data:", responseData);
    return responseData;
  } catch (e) {
    console.error("Unexpected Error:", e.message);
    handleFetchError(e);
  }
};

const deleteData = async (route, token) => {
  try {
    const URL_ROUTE = `${API_URL}${route}`.replace(/\/+$/, "");

    console.log("Sending DELETE Request to:", URL_ROUTE);

    const response = await fetch(URL_ROUTE, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      handleUnauthorizedRedirection(route);
      return;
    }

    console.log("Response Status:", response.status);

    if (!response.ok) {
      const responseData = await response.json().catch(() => ({}));
      if (response.status === 403 && responseData?.code === 'SUBSCRIPTION_EXPIRED') {
        window.location.href = '/subscription-expired';
        return responseData;
      }
      console.error("Response Error:", responseData);
      return {
        success: false,
        message: responseData?.message || `Error: ${response.status}`
      };
    }

    console.log("User deleted successfully!");
    return { success: true, message: "Deleted successfully" };
  } catch (e) {
    console.error("Unexpected Error:", e.message);
    handleFetchError(e);
  }
};

const uploadFile = async (userId, file) => {
  const URL_ROUTE = `${API_URL}${userId}`;
  const formData = new FormData();
  formData.append("file", file, file.name);

  try {
    const response = await fetch(URL_ROUTE, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: `Server Error: ${response.status} ${response.statusText}`
      };
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("File Upload Error:", error);
    return handleUploadError(error);
  }
};

export { getData, postData, putData, deleteData, uploadFile, postDataBulkUpload, patchData };
